// What the create-and-edit sheets are made of, worked out from the payloads.
// Pure: no React, no clock, no I/O, so every rule below is unit-testable on its
// own - the same split `day/day-model.ts` keeps.
//
// The one idea worth reading first: this file is the ONLY place the server's
// weekday numbering and this client's meet. `recurrenceSpecSchema` numbers
// weekdays 1 (Sunday) to 7 (Saturday); `weekdayIndex()` in iso-day.ts numbers
// them 0 (Sunday) to 6 (Saturday), because that is the order `WEEKDAYS` is
// written in there. A component that converted between them itself would be a
// second copy of an off-by-one, which is the class of bug nobody sees until a
// Monday item lands on a Sunday.

import type { DaySection, RecurrenceSpec, Routine, Section } from '../api/client.ts';

/**
 * The eight schedule hues, each in both modes, as `#RRGGBB`.
 *
 * ALL SIXTEEN ARE THE PROTOTYPE'S, verbatim - `[data-theme="light"]` and
 * `[data-theme="dark"]` at the head of `prototype/prototype.html`, where the
 * design doc reports every one contrast-checked against every surface
 * (`prototype-v1.md` §3). NOT ONE IS COMPUTED. A dark value is not a lightened
 * light one: `#2F5FBF` and `#93B5F2` were checked as a pair against the papers
 * they each sit on, and deriving either from the other would break the pair
 * that measurement was made on. If a ninth hue is ever added it arrives with
 * both of its values or it does not arrive.
 *
 * `hex` IS THE ONE THE DATABASE HOLDS, and `dark` never goes anywhere near it.
 * `section.color_hex` says WHICH colour a schedule is; this file says what that
 * colour looks like in each mode. Keeping the second half here rather than in
 * the payload is what stops a colour change becoming a data migration, and what
 * stops two clients disagreeing about what green is.
 *
 * The names are labels for the picker only. Nothing sends them anywhere.
 */
export const PALETTE: readonly { name: string; hex: string; dark: string }[] = [
  { name: 'Blue', hex: '#2F5FBF', dark: '#93B5F2' },
  { name: 'Amber', hex: '#8F4E0C', dark: '#E2A468' },
  { name: 'Green', hex: '#1C7549', dark: '#6FCB9B' },
  { name: 'Purple', hex: '#6A47A6', dark: '#B79BE8' },
  { name: 'Rose', hex: '#AB3557', dark: '#E890A7' },
  { name: 'Teal', hex: '#0F6D6B', dark: '#6CC7C3' },
  { name: 'Slate', hex: '#455168', dark: '#A7B3C6' },
  { name: 'Clay', hex: '#7C4E34', dark: '#D3A184' },
];

/** Case-insensitive, because a hex is stored as typed and `#2f5fbf` is the same colour. */
const DARK_BY_LIGHT = new Map(PALETTE.map((hue) => [hue.hex.toUpperCase(), hue.dark]));

/**
 * The hue to PAINT, given the hue that was STORED and the mode on screen.
 *
 * WHY THIS EXISTS AT ALL, since a tint already follows the theme: `--ct` is
 * mixed against the live `--surface` and so has always been right, but `--c`
 * itself is the stored hex and was painting its LIGHT value on a dark page.
 * Measured the way `prototype-v1.md` §3 measures, the worst hue pair fell from
 * 5.2:1 in light to 2.2:1 in dark - under the 3:1 that non-text UI wants, in a
 * design whose recorded promise is that nothing is below 4.5:1. Dark is not
 * opt-in either: a device set to dark gets it without anyone choosing a theme.
 *
 * AN UNKNOWN HEX IS RETURNED UNCHANGED, deliberately. `colorSchema` on the
 * server accepts any `#RRGGBB`, so a schedule may hold a colour this palette
 * has never heard of - and the only honest answer for one is the colour that
 * was asked for. Computing a dark counterpart is exactly the invention the
 * whole pairing above exists to avoid; a made-up value that no one checked is
 * worse than a real one that is merely dim.
 */
export function hueIn(hex: string, mode: 'light' | 'dark'): string {
  if (mode === 'light') return hex;
  return DARK_BY_LIGHT.get(hex.toUpperCase()) ?? hex;
}

/**
 * The icons a schedule may be given, in the prototype's order.
 *
 * Every name here is a key in `day/Icon.tsx`. An unknown one is not an error
 * there - it falls back to a plain circle, exactly as migration 0001's default
 * does - so a schedule made by another client can never fail to draw.
 */
export const ICONS: readonly string[] = [
  'briefcase',
  'book',
  'dumbbell',
  'music',
  'pot',
  'leaf',
  'heart',
  'home',
  'star',
  'palette',
  'pencil',
  'clock',
];

/** The prototype's own suggestions, for the first field of an empty account. */
export const NAME_SUGGESTIONS: readonly string[] = [
  'Music practice',
  'Meal prep',
  'Garden',
  'Physio',
  'Chores',
];

/** Sunday first, matching `weekdayIndex()`. Two Ts and two Ss, as a week has. */
export const WEEKDAY_INITIALS: readonly string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Spoken, for the picker's labels - an initial is not a name to a screen reader. */
export const WEEKDAY_NAMES: readonly string[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * The four repeats the design offers, and the only ones this client writes.
 *
 * The API accepts more than these - monthly, yearly, every N weeks - and the
 * server can describe all of them. The chips deliberately do not: the bar is a
 * stranger understanding this in sixty seconds, and a rule these cannot express
 * is left alone rather than quietly rewritten (see `repeatOf`).
 */
export type Repeat = 'every' | 'weekdays' | 'some' | 'once';

export const REPEATS: readonly { key: Repeat; label: string }[] = [
  { key: 'every', label: 'Every day' },
  { key: 'weekdays', label: 'Weekdays' },
  { key: 'some', label: 'Some days' },
  { key: 'once', label: 'Just once' },
];

/** Monday to Friday, as `weekdayIndex()` numbers them. */
const MON_TO_FRI: readonly number[] = [1, 2, 3, 4, 5];

function sameDays(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((d) => set.has(d));
}

/**
 * The rule to send for a chosen repeat.
 *
 * `once` is `null` - a one-off is the ABSENCE of a rule, which is also what
 * makes the server describe it as 'Just once' and set the `once` flag on the
 * day item. There is no `freq: 'once'`.
 *
 * `days` are this client's indices; the +1 here is the only place they become
 * the server's.
 */
export function toRule(repeat: Repeat, days: readonly number[]): RecurrenceSpec | null {
  switch (repeat) {
    case 'once':
      return null;
    case 'every':
      return { freq: 'daily', interval: 1 };
    case 'weekdays':
      return { freq: 'weekly', interval: 1, weekdays: MON_TO_FRI.map((d) => d + 1) };
    case 'some':
      return {
        freq: 'weekly',
        interval: 1,
        weekdays: [...days].sort((a, b) => a - b).map((d) => d + 1),
      };
  }
}

/** Which days a chosen repeat is really about, so the picker agrees with the chip. */
export interface RepeatChoice {
  repeat: Repeat;
  /** This client's indices, 0 (Sunday) to 6 (Saturday). Empty unless `some`. */
  days: number[];
}

/**
 * The chip that describes a stored rule, or null when none of them does.
 *
 * Null is the case worth having: the API accepts monthly, yearly, every-N and
 * end-dated rules that these four chips cannot say, and a sheet that silently
 * showed one of them as "Every day" would rewrite the rule the moment anything
 * was saved. The item sheet reads null as "leave the repeat alone", shows the
 * server's own words for it instead, and sends no `rule` unless a chip is
 * actually pressed.
 */
export function repeatOf(rule: RecurrenceSpec | null): RepeatChoice | null {
  if (rule === null) return { repeat: 'once', days: [] };

  // A last day is the most damaging thing to read past: none of the chips can
  // say one, so the rule they would send back drops `endDay` - and a repeat
  // that was going to stop in December becomes one that never stops, from an
  // edit to the title. An explicit null is "forever", which they can say.
  if (rule.endDay !== undefined && rule.endDay !== null) return null;

  const interval = rule.interval ?? 1;
  if (interval !== 1) return null;

  if (rule.freq === 'daily') return { repeat: 'every', days: [] };
  if (rule.freq !== 'weekly') return null;

  const weekdays = rule.weekdays ?? [];
  // A weekly rule with no weekday list means "the same weekday as the day it
  // started on", which no chip here says either.
  if (weekdays.length === 0) return null;

  const days = [...new Set(weekdays)].map((d) => d - 1).sort((a, b) => a - b);
  if (days.some((d) => d < 0 || d > 6)) return null;
  if (sameDays(days, MON_TO_FRI)) return { repeat: 'weekdays', days: [] };
  return { repeat: 'some', days };
}

/**
 * Whether a repeat is ready to be written.
 *
 * "Some days" with nothing picked is the trap this exists for: an empty weekday
 * list on a weekly rule is legal, and the server reads it as "every week on the
 * day it started" - so pressing Create would produce a weekly item nobody asked
 * for. It is refused here rather than sent and explained afterwards.
 */
export function repeatIsComplete(repeat: Repeat, days: readonly number[]): boolean {
  return repeat !== 'some' || days.length > 0;
}

export function toggleDay(days: readonly number[], day: number): number[] {
  return days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b);
}

/**
 * The schedules a new item may be put in, in the order the day screen draws
 * them: `sort_index`, then name, then id - the same tail `readDay` orders by,
 * so the picker cannot list them differently from the cards behind it.
 *
 * Archived schedules are left out. Restoring one is a deliberate act, and
 * putting a new item into something that is off the day would file it where
 * nothing shows it.
 */
export function choosableSections(sections: readonly Section[]): Section[] {
  return sections
    .filter((s) => s.archivedAt === null)
    .sort(
      (a, b) =>
        a.sortIndex - b.sortIndex || a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
    );
}

/**
 * Every schedule on the account, active ones first, each group in the order the
 * day screen draws them: `sort_index`, then name, then id - the same tail
 * `readDay` orders by.
 *
 * `choosableSections` above answers a different question and must not be reused
 * for this one: it is "where may a new thing go", so it drops the archived. This
 * is "what does this account have", which is the list Settings shows - and the
 * archived half is the point of it, because an archived schedule is otherwise
 * reachable only through the ten seconds an Undo is on screen.
 *
 * The server already orders them this way (`listSections`); sorting again here
 * is what stops that being a rule two places agree on by luck.
 */
export function scheduleList(sections: readonly Section[]): Section[] {
  const order = (a: Section, b: Section) =>
    a.sortIndex - b.sortIndex || a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
  return [
    ...sections.filter((s) => s.archivedAt === null).sort(order),
    ...sections.filter((s) => s.archivedAt !== null).sort(order),
  ];
}

/**
 * A schedule as the edit sheet works on it, from wherever it was reached.
 *
 * The sheet is opened from two places that hold two different shapes of the
 * same row: a card on the day, which is a `DaySection` and knows what is due in
 * it, and the account's list, which is a `Section` from `GET /sections` and has
 * no day behind it at all. Everything the sheet EDITS - the name, the colour,
 * the icon - is on both, and the one thing it only DRAWS is the count of what
 * is due today.
 *
 * So the count is `number | null` rather than a number, and null means "no day
 * asked this question" rather than "nothing due". A `Section` cast into a
 * `DaySection` would have to invent an empty `items` array, and the sheet would
 * then tell somebody that a schedule they use every Sunday has nothing in it.
 */
export interface EditableSchedule {
  id: string;
  name: string;
  colorHex: string;
  symbol: string;
  /** What is due in it on the day being looked at, or null when there is no day. */
  dueOnDay: number | null;
}

/** A schedule reached from its card, which is the day's own answer for the count. */
export function editableFromDay(section: DaySection): EditableSchedule {
  return {
    id: section.id,
    name: section.name,
    colorHex: section.colorHex,
    symbol: section.symbol,
    dueOnDay: section.items.length,
  };
}

/** A schedule reached from the account's list, where no day has been read. */
export function editableFromAccount(section: Section): EditableSchedule {
  return {
    id: section.id,
    name: section.name,
    colorHex: section.colorHex,
    symbol: section.symbol,
    dueOnDay: null,
  };
}

/**
 * Whether this row may be edited by the sheets here at all.
 *
 * A STEP may not: `refuseStepWrite` answers `steps-arrive-in-phase-2` for every
 * write aimed at one, and the refusal is the API's rather than a limitation to
 * route around (AGENTS.md). Nothing in this client opens a sheet on a step -
 * the day screen ticks them and draws them and offers no edit - so this is the
 * belt to that braces: a routine fetched by id is asked before it is rendered.
 *
 * A REMINDER may not either, for a plainer reason: the reminders block is not
 * built, so there is no screen to open one from and no sheet shaped for one.
 */
export function isEditableItem(routine: Routine): boolean {
  return routine.parentRoutineId === null && routine.sectionId !== null;
}

/** '3 things', '1 thing' - the count under a schedule's name in its sheet. */
export function thingCount(n: number): string {
  return `${n} thing${n === 1 ? '' : 's'}`;
}
